export * from "./types";
export * from "./costs";
export * from "./businessLogic";
export * from "./assessments";

import type { RIASSchemes, RIASScheme } from "./types";
import { RIASRecordType } from "./types";

const { default: raw } = await import("~/data/schemes.json");
const schemes = raw as RIASSchemes;


export const orderedJoin = (target: any, parts: string[], join: string) =>
	!target
		? ""
		: parts
				.map(key => target[key])
				.filter(value => value != null && String(value).trim() !== "")
				.join(join);

export const parseCertificateString = (input: string) => {
	// Split on first hyphen only
	const [idPart, suffixRaw = ""] = input.split(/-(.+)/);

	// Extract the C + digits, and also extract the digits alone
	const idMatch = idPart.match(/^C(\d+)$/i);

	const riasId = idMatch ? Number(idMatch[1]) : null;

	// Extract Stage / Amendment (case-insensitive, typo-flexible)
	const stageMatch = suffixRaw.match(/stage\s*(\d+)/i);
	const amendMatch = suffixRaw.match(/amend\w*\s*(\d+)/i);

	return {
		riasId, // numeric value
		stage: stageMatch ? Number(stageMatch[1]) : null,
		amendment: amendMatch ? Number(amendMatch[1]) : null,
	};
};

export const schemeData = (schemeSlug: string): RIASScheme|null => {
	let schemeKey: keyof RIASSchemes|undefined = undefined;
	
	switch ( schemeSlug ) {
		case "s6Domestic":
		case "s6NonDomestic":
		case "s6NonDomesticAdv":
			schemeKey = schemeSlug as keyof RIASSchemes;
		
		case "section-6-domestic":
			schemeKey = "s6Domestic";
			break;
		case "section-6-non-domestic":
			schemeKey = "s6NonDomestic";
			break;
		case "section-6-non-domestic-advanced":
			schemeKey = "s6NonDomesticAdv";
			break;
		case "cpd":
			schemeKey = "cpd";
			break;			
	}

	return schemeKey ? schemes[schemeKey] : null;
};

export const formattedTimestamp = (ts: number, format: any ={
		day: "2-digit",
		month: "short",
		year: "numeric",
} ) =>  ts == 0 ? "n/a" : new Intl.DateTimeFormat("en-GB", format).format(new Date(ts < 2525999000 ? ts * 1000 : ts));
	
	


export const clietnEnv = {
	NODE_ENV: process.env.NODE_ENV ?? "development",
};

export const ordinal = (n: number): string => {
	if (n == 0) return "0";

	const pr = new Intl.PluralRules("en", { type: "ordinal" });
	const suffixes = { one: "st", two: "nd", few: "rd", other: "th" };

	return n + suffixes[pr.select(n)];
};

const months = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];

export const formatLongDate = (timestamp: number) => {
	if (timestamp < 2525999000) timestamp *= 1000;

	const d = new Date(timestamp); // timestamp in ms
	const month = months[d.getMonth()];
	const dayOrdinal = ordinal(d.getDate());
	const year = d.getFullYear();
	return `${dayOrdinal} ${month}, ${year}`;
};

export const generateCodeVerifier = () => {
	const array = new Uint32Array(56);
	crypto.getRandomValues(array);
	return Array.from(array, dec => ("0" + dec.toString(16)).slice(-2)).join("");
};

export const generateCodeChallenge = async (verifier: string) => {
	const encoder = new TextEncoder();
	const data = encoder.encode(verifier);
	const digest = await crypto.subtle.digest("SHA-256", data);
	return btoa(String.fromCharCode(...new Uint8Array(digest)))
		.replace(/\+/g, "-")
		.replace(/\//g, "_")
		.replace(/=+$/, "");
};

export const padId = (id: number, recordType: RIASRecordType | null = null) =>
	`${recordType ? recordType : ""}${recordType ? "#" : ""}${String(id).padStart(
		7,
		"0"
	)}`;

export const toSlug = (s: string) =>
	s.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();

export const toCamel = (s: string) =>
	s.replace(/-([a-z])/g, (_, c) => c.toUpperCase());

// --- helper parser ---
export const parsePlotRanges = (input, maxPlot = 9999) => {
	const errors = [];
	const result = new Set();

	const clean = input.replace(/[–—]/g, "-"); // normalize dash variants
	const tokens = clean
		.split(",")
		.map(t => t.trim())
		.filter(Boolean);

	for (const token of tokens) {
		const match = token.match(/^(\d+)(?:-(\d+))?$/);
		if (!match) {
			errors.push(`Invalid token "${token}"`);
			continue;
		}

		let [, start, end] = match.map(Number);
		if (!end) end = start;
		if (end < start) [start, end] = [end, start];

		for (let i = start; i <= end; i++) {
			if (i < 1 || i > maxPlot) {
				errors.push(`Plot ${i} out of bounds`);
				continue;
			}
			result.add(i);
		}
	}

	return { plots: Array.from(result).sort((a, b) => a - b), errors };
};

// --- convert plots array back to compact range string ---
export const plotsToRanges = plots => {
	if (!plots.length) return "";
	plots.sort((a, b) => a - b);
	const ranges = [];
	let start = plots[0];
	let prev = plots[0];
	for (let i = 1; i <= plots.length; i++) {
		if (plots[i] !== prev + 1) {
			ranges.push(start === prev ? `${start}` : `${start}-${prev}`);
			start = plots[i];
		}
		prev = plots[i];
	}
	return ranges.join(", ");
};
